import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {StepProps} from '../IAppModels'

const Step2 = ({
  data,
  updateData,
  onValidationChange,
}: StepProps & {onValidationChange: (isValid: boolean) => void}) => {
  const [hoveredInput, setHoveredInput] = useState('')
  const {formatMessage: f} = useIntl()

  const getInputStyle = (inputName: string) => {
    return {
      backgroundColor: 'white',
      border: '1px solid #e4e6ef',
      transition: 'all 0.3s ease',
      ...(hoveredInput === inputName && {
        borderColor: '#059BB6',
        boxShadow: '0 0 5px rgba(5, 155, 182, 0.5)',
      }),
    }
  }

  const handleCostSavingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '')
    const formattedValue = new Intl.NumberFormat('id-ID').format(Number(value))
    updateData({...data, appBasic: {...data.appBasic, costSaving: Number(value)}})
    e.target.value = formattedValue
  }

  useEffect(() => {
    const isValid = validateData()
    onValidationChange(isValid)
  }, [data, onValidationChange])

  const validateData = () => {
    // All fields optional
    return true
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  return (
    <div data-kt-stepper-element='content'>
      <div className='w-100'>
        {/* Laboratory Section */}
        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.LABORATORY'})} </label>
          <textarea
            className='form-control form-control-lg'
            name='laboratorium'
            placeholder=''
            value={data.appBasic.laboratorium}
            onKeyDown={handleKeyDown}
            onChange={(e) =>
              updateData({
                appBasic: {
                  ...data.appBasic,
                  laboratorium: e.target.value,
                },
              })
            }
            style={getInputStyle('laboratorium')}
            onMouseEnter={() => setHoveredInput('laboratorium')}
            onMouseLeave={() => setHoveredInput('')}
          />
        </div>

        {/* General Condition Section */}
        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.GENERAL_CONDITION'})} </label>
          <textarea
            className='form-control form-control-lg'
            name='generalCondition'
            placeholder=''
            value={data.appBasic.generalCondition}
            onChange={(e) =>
              updateData({
                appBasic: {
                  ...data.appBasic,
                  generalCondition: e.target.value,
                },
              })
            }
            style={getInputStyle('generalCondition')}
            onMouseEnter={() => setHoveredInput('generalCondition')}
            onMouseLeave={() => setHoveredInput('')}
          />
        </div>

        {/* Medical Staff Section */}
        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.DOCTOR'})} </label>
          <input
            type='text'
            className='form-control'
            value={data.appBasic.doctor}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, doctor: e.target.value}})
            }
            style={getInputStyle('doctor')}
            onMouseEnter={() => setHoveredInput('doctor')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.SPECIALIST'})} </label>
          <input
            type='text'
            className='form-control'
            value={data.appBasic.specialist}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, specialist: e.target.value}})
            }
            style={getInputStyle('specialist')}
            onMouseEnter={() => setHoveredInput('specialist')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Cost Information Section */}
        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.MEDICAL_EXPENSE'})} </label>
          <input
            type='text'
            className='form-control'
            value={
              data.appBasic.medicalExpense
                ? data.appBasic.medicalExpense.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            onChange={(e) =>
              updateData({
                ...data,
                appBasic: {
                  ...data.appBasic,
                  medicalExpense: Number(e.target.value.replace(/[^0-9]/g, '')),
                },
              })
            }
            style={getInputStyle('medicalExpense')}
            onMouseEnter={() => setHoveredInput('medicalExpense')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.UPGRADE_ROOM'})} </label>
          <input
            type='text'
            className='form-control'
            value={data.appBasic.upgradeRoom}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, upgradeRoom: e.target.value}})
            }
            style={getInputStyle('upgradeRoom')}
            onMouseEnter={() => setHoveredInput('upgradeRoom')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.REASON'})} </label>
          <input
            type='text'
            className='form-control'
            value={data.appBasic.reason}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, reason: e.target.value}})
            }
            style={getInputStyle('reason')}
            onMouseEnter={() => setHoveredInput('reason')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.IS_APS'})} </label>
          <select
            className='form-control'
            value={data.appBasic.isAPS ? 'Yes' : 'No'}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, isAPS: e.target.value === 'Yes'}})
            }
            style={getInputStyle('isAPS')}
            onMouseEnter={() => setHoveredInput('isAPS')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          >
            <option value='Yes'>Yes</option>
            <option value='No'>No</option>
          </select>
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.COST_SAVING'})} </label>
          <input
            type='text'
            className='form-control'
            value={
              data.appBasic.costSaving
                ? data.appBasic.costSaving.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            onChange={handleCostSavingChange}
            style={getInputStyle('costSaving')}
            onMouseEnter={() => setHoveredInput('costSaving')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.REMARKS_COST_SAVING'})} </label>
          <input
            type='text'
            className='form-control'
            value={data.appBasic.remarksCostSaving}
            onChange={(e) =>
              updateData({
                ...data,
                appBasic: {...data.appBasic, remarksCostSaving: e.target.value},
              })
            }
            style={getInputStyle('remarksCostSaving')}
            onMouseEnter={() => setHoveredInput('remarksCostSaving')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Vital Signs Section */}
        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.PULSE'})} </label>
          <input
            type='number'
            className='form-control form-control-lg'
            name='pulse'
            value={data.appBasic.pulse}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, pulse: e.target.value}})
            }
            style={getInputStyle('pulse')}
            onMouseEnter={() => setHoveredInput('pulse')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.RESPIRATORY'})} </label>
          <input
            type='number'
            className='form-control form-control-lg'
            name='respiratory'
            value={data.appBasic.respiratory}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, respiratory: e.target.value}})
            }
            style={getInputStyle('respiratory')}
            onMouseEnter={() => setHoveredInput('respiratory')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.BLOOD_PRESSURE'})} </label>
          <div className='d-flex align-items-center'>
            <input
              type='number'
              className='form-control form-control-lg me-2'
              style={{...getInputStyle('bloodPressureSystolic')}}
              placeholder='Systolic'
              value={data.appBasic.bloodPressure.split('/')[0] || ''}
              onChange={(e) => {
                const systolic = e.target.value
                const diastolic = data.appBasic.bloodPressure.split('/')[1] || ''
                updateData({
                  ...data,
                  appBasic: {
                    ...data.appBasic,
                    bloodPressure: `${systolic}/${diastolic}`,
                  },
                })
              }}
              onMouseEnter={() => setHoveredInput('bloodPressureSystolic')}
              onMouseLeave={() => setHoveredInput('')}
              onKeyDown={handleKeyDown}
            />
            <span className='fs-4 fw-bold mx-1'>/</span>
            <input
              type='number'
              className='form-control form-control-lg me-2'
              style={{...getInputStyle('bloodPressureDiastolic')}}
              placeholder='Diastolic'
              value={data.appBasic.bloodPressure.split('/')[1] || ''}
              onChange={(e) => {
                const systolic = data.appBasic.bloodPressure.split('/')[0] || ''
                const diastolic = e.target.value
                updateData({
                  ...data,
                  appBasic: {
                    ...data.appBasic,
                    bloodPressure: `${systolic}/${diastolic}`,
                  },
                })
              }}
              onMouseEnter={() => setHoveredInput('bloodPressureDiastolic')}
              onMouseLeave={() => setHoveredInput('')}
              onKeyDown={handleKeyDown}
            />
            <span className='fs-6 text-gray-600'>mmHg</span>
          </div>
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.TEMPERATURE'})} </label>
          <div className='d-flex align-items-center'>
            <input
              type='number'
              className='form-control form-control-lg me-2'
              name='temperature'
              value={data.appBasic.temperature}
              onChange={(e) =>
                updateData({...data, appBasic: {...data.appBasic, temperature: e.target.value}})
              }
              style={getInputStyle('temperature')}
              onMouseEnter={() => setHoveredInput('temperature')}
              onMouseLeave={() => setHoveredInput('')}
              onKeyDown={handleKeyDown}
            />
            <span className='fs-6 text-gray-600'>°C</span>
          </div>
        </div>

        {/* Treatment Information Section */}
        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.SUPPORTING'})} </label>
          <input
            type='text'
            className='form-control form-control-lg'
            name='supporting'
            value={data.appBasic.supporting}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, supporting: e.target.value}})
            }
            style={getInputStyle('supporting')}
            onMouseEnter={() => setHoveredInput('supporting')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.FOLLOW_UP'})} </label>
          <input
            type='text'
            className='form-control form-control-lg'
            name='followUp'
            value={data.appBasic.followUp}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, followUp: e.target.value}})
            }
            style={getInputStyle('followUp')}
            onMouseEnter={() => setHoveredInput('followUp')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.TREATMENT'})} </label>
          <input
            type='text'
            className='form-control form-control-lg'
            name='treatment'
            value={data.appBasic.treatment}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, treatment: e.target.value}})
            }
            style={getInputStyle('treatment')}
            onMouseEnter={() => setHoveredInput('treatment')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className='fv-row mb-10'>
          <label className='form-label'>{f({id: 'CLAIM.INPATIENT.REMARK'})} </label>
          <input
            type='text'
            className='form-control form-control-lg'
            name='remarks'
            value={data.appBasic.remarks}
            onChange={(e) =>
              updateData({...data, appBasic: {...data.appBasic, remarks: e.target.value}})
            }
            style={getInputStyle('remarks')}
            onMouseEnter={() => setHoveredInput('remarks')}
            onMouseLeave={() => setHoveredInput('')}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
    </div>
  )
}

export {Step2}